import { palette, shades } from "lib/miolo";
import formatMoneyValueOnlyDigits from "components/utils/formatMoneyValueOnlyDigits";
import { getYear } from "date-fns";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import { last } from "lodash";
import tinycolor2 from "tinycolor2";

interface IDebtObligationsListProps {
  debtObligations: IBrazilianCompanyIssuerTopLevelFragment["debtObligations"];
}

export default function DebtObligationsList({ debtObligations }: IDebtObligationsListProps) {
  const lastDebtObligationNode = last(debtObligations);

  return (
    <table
      className="w-full"
      css={{
        borderRadius: 4,
        borderSpacing: 0,
        "> thead > tr > th": {
          padding: "6px 12px",
          fontWeight: 700,
          fontSize: 14,
          lineHeight: 1.33,
        },
        "& > tbody > tr > td": {
          padding: "6px 12px",
          lineHeight: 1.33,
        },
      }}
    >
      <thead>
        <tr>
          <th
            css={{
              color: palette.white,
              lineHeight: "18px",
              backgroundColor: palette.blueBayoux,
              borderRight: `solid 1px ${palette.lynch}`,
            }}
          >
            Ano
          </th>

          <th
            css={{
              color: palette.blueBayoux,
              fontWeight: 700,
              lineHeight: "18px",
              backgroundColor: palette.catskillWhite,
              borderRight: `solid 1px ${palette.mystic}`,
            }}
          >
            Inferior à 1 ano
          </th>

          <th
            css={{
              color: palette.blueBayoux,
              fontWeight: 700,
              lineHeight: "18px",
              backgroundColor: palette.catskillWhite,
              borderRight: `solid 1px ${palette.mystic}`,
            }}
          >
            De 1 a 3 anos
          </th>

          <th
            css={{
              color: palette.blueBayoux,
              fontWeight: 700,
              lineHeight: "18px",
              backgroundColor: palette.catskillWhite,
              borderRight: `solid 1px ${palette.mystic}`,
            }}
          >
            De 3 a 5 anos
          </th>

          <th
            css={{
              color: palette.blueBayoux,
              fontWeight: 700,
              lineHeight: "18px",
              backgroundColor: palette.catskillWhite,
              borderRight: `solid 1px ${palette.mystic}`,
            }}
          >
            Mais de 5 anos
          </th>

          <th
            css={{
              color: palette.mojo,
              fontWeight: 700,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            Total
          </th>
        </tr>
      </thead>

      <tbody
        css={{
          "& > tr > td": {
            textAlign: "center",
          },
          "& > tr > td:not(:first-of-type)": {
            borderBottom: `solid 1px ${palette.mystic}`,
            borderLeft: `solid 1px ${palette.mystic}`,
          },
          "& > tr > td:last-of-type": {
            borderRight: "transparent",
          },
          "& > tr:hover": {
            backgroundColor: "rgb(241, 244, 248, 0.5)",
          },
          "& > tr:last-of-type > td": {
            borderBottom: "none",
          },
        }}
      >
        {debtObligations.map((node) => {
          return (
            <tr
              key={node.fiscalYearStart}
              css={{
                "& > td": {
                  padding: "8px 12px",
                  fontWeight: 700,
                  fontSize: 14,
                  fontFamily: "novel-mono-pro-condensed",
                  lineHeight: "20px",
                },
              }}
            >
              <td
                css={{
                  color: palette.white,
                  backgroundColor: palette.blueBayoux,
                  borderTop: `solid 1px ${shades.greys.variants[4]}`,
                  borderRight: `solid 1px ${shades.greys.variants[4]}`,
                  borderBottom: `solid 1px ${shades.greys.variants[4]}`,
                }}
              >
                <span>{getYear(parseDateYmd(node.fiscalYearStart))}</span>
              </td>

              <td
                css={{
                  color: palette.lynch,
                  border: `solid 1px ${shades.greys.variants[7]}`,
                }}
              >
                {formatMoneyValueOnlyDigits(node.lessThanOneYear / 1000 / 1000)}
              </td>

              <td
                css={{
                  color: palette.lynch,
                  border: `solid 1px ${shades.greys.variants[7]}`,
                }}
              >
                {formatMoneyValueOnlyDigits(node.oneToThreeYears / 1000 / 1000)}
              </td>

              <td
                css={{
                  color: palette.lynch,
                  border: `solid 1px ${shades.greys.variants[7]}`,
                }}
              >
                {formatMoneyValueOnlyDigits(node.threeToFiveYears / 1000 / 1000)}
              </td>

              <td
                css={{
                  color: palette.lynch,
                  border: `solid 1px ${shades.greys.variants[7]}`,
                }}
              >
                {formatMoneyValueOnlyDigits(node.moreThanFiveYears / 1000 / 1000)}
              </td>

              <td
                css={{
                  color: palette.mojo,
                  border: "solid 1px #eec9c9",
                  backgroundColor: tinycolor2(palette.vanillaIce).lighten(5).toHexString(),
                }}
              >
                {formatMoneyValueOnlyDigits(node.totalDebt / 1000 / 1000)}
              </td>
            </tr>
          );
        })}

        {lastDebtObligationNode && lastDebtObligationNode.totalDebt !== 0 && (
          <tr
            css={{
              "& > td": {
                padding: "8px 12px",
                fontWeight: 700,
                fontSize: 14,
                fontFamily: "novel-mono-pro-condensed",
                lineHeight: "20px",
              },
            }}
          >
            <td
              css={{
                color: palette.white,
                backgroundColor: palette.blueBayoux,
                borderTop: `solid 1px ${shades.greys.variants[4]}`,
                borderRight: `solid 1px ${shades.greys.variants[4]}`,
                borderBottom: `solid 1px ${shades.greys.variants[4]}`,
              }}
            >
              Total
            </td>

            <td
              css={{
                color: palette.mojo,
                border: `solid 1px ${shades.greys.variants[7]}`,
              }}
            >
              {Math.round((lastDebtObligationNode.lessThanOneYear / lastDebtObligationNode.totalDebt) * 100).toFixed(0)}
              %
            </td>

            <td
              css={{
                color: palette.mojo,
                border: `solid 1px ${shades.greys.variants[7]}`,
              }}
            >
              {Math.round((lastDebtObligationNode.oneToThreeYears / lastDebtObligationNode.totalDebt) * 100).toFixed(0)}
              %
            </td>

            <td
              css={{
                color: palette.mojo,
                border: `solid 1px ${shades.greys.variants[7]}`,
              }}
            >
              {Math.round((lastDebtObligationNode.threeToFiveYears / lastDebtObligationNode.totalDebt) * 100).toFixed(
                0,
              )}
              %
            </td>

            <td
              css={{
                color: palette.mojo,
                border: `solid 1px ${shades.greys.variants[7]}`,
              }}
            >
              {Math.round((lastDebtObligationNode.moreThanFiveYears / lastDebtObligationNode.totalDebt) * 100).toFixed(
                0,
              )}
              %
            </td>

            <td
              css={{
                color: palette.mojo,
                border: "solid 1px #eec9c9",
                backgroundColor: tinycolor2(palette.vanillaIce).lighten(5).toHexString(),
              }}
            >
              100%
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
